import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, LogOut } from "lucide-react";

export default function ForcedPasswordChange() {
  const { user, logout, finishForcedChange } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("Nieuw wachtwoord moet minimaal 8 tekens zijn"); return; }
    if (next !== confirm) { setError("De nieuwe wachtwoorden komen niet overeen"); return; }
    if (next === current) { setError("Nieuw wachtwoord moet verschillen van het huidige"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Wachtwoord ingesteld — je bent nu ingelogd");
      finishForcedChange();
    } catch (err) {
      setError(err?.response?.data?.detail || "Wijzigen mislukt");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-10" data-testid="forced-password-screen">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-amber-400 text-amber-950 px-6 py-4 flex items-center gap-3">
          <ShieldAlert className="h-6 w-6 shrink-0" strokeWidth={2.2} />
          <div>
            <p className="font-heading font-bold text-base leading-tight">Wachtwoord wijzigen verplicht</p>
            <p className="text-xs text-amber-900/90">{user?.username ? `Ingelogd als ${user.username}` : ""}</p>
          </div>
        </div>
        <div className="px-6 pt-5 pb-6">
          <p className="text-sm text-zinc-500 mb-4" data-testid="forced-password-explanation">
            Je gebruikt nog een tijdelijk wachtwoord. Om je account te beveiligen moet je eerst een
            eigen wachtwoord instellen. Daarna ga je meteen door — je hoeft niet opnieuw in te loggen.
          </p>
          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Huidig (tijdelijk) wachtwoord</label>
              <input data-testid="fpc-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} autoComplete="current-password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Nieuw wachtwoord</label>
              <input data-testid="fpc-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} autoComplete="new-password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Bevestig nieuw wachtwoord</label>
              <input data-testid="fpc-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" />
            </div>
            {error && <div data-testid="fpc-error" className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 font-medium">{error}</div>}
            <button data-testid="fpc-submit" type="submit" disabled={saving} className="w-full px-4 py-2.5 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
              {saving ? "Bezig…" : "Wachtwoord instellen en doorgaan"}
            </button>
          </form>
          <button data-testid="fpc-logout" onClick={logout} className="mt-4 w-full flex items-center justify-center gap-2 text-xs font-medium text-zinc-500 hover:text-zinc-700">
            <LogOut className="h-3.5 w-3.5" /> Uitloggen (verkeerd account)
          </button>
        </div>
      </div>
    </div>
  );
}
